import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  useMediaQuery,
  Fade
} from '@mui/material';
import {
  Inbox as InboxIcon,
  SearchOff as SearchOffIcon,
  ErrorOutline as ErrorIcon,
  CloudOff as CloudOffIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import StandardButton from './StandardButton';

// Styled Components Mobile First
const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: theme.spacing(3),
  minHeight: '250px',
  width: '100%',
  // Mobile first
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
    minHeight: '300px',
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(6),
    minHeight: '350px',
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(8),
    minHeight: '400px',
  }
}));

const IconContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  color: theme.palette.text.secondary,
  '& svg': {
    fontSize: '3rem',
    // Mobile first
    [theme.breakpoints.up('sm')]: {
      fontSize: '3.5rem',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '4rem',
    }
  }
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  maxWidth: '320px',
  margin: '0 auto',
  // Mobile first
  [theme.breakpoints.up('sm')]: {
    maxWidth: '400px',
  },
  [theme.breakpoints.up('md')]: {
    maxWidth: '480px',
  }
}));

// Container para APENAS UM botão
const ActionContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  display: 'flex',
  justifyContent: 'center',
  // Mobile first
  [theme.breakpoints.up('sm')]: {
    marginTop: theme.spacing(4),
  }
}));

// Ícones pré-definidos
const EmptyStateIcons = {
  default: InboxIcon,
  search: SearchOffIcon,
  error: ErrorIcon,
  offline: CloudOffIcon,
  inbox: InboxIcon
};

// Mensagens pré-definidas com APENAS AÇÃO PRINCIPAL
const EmptyStateMessages = {
  default: {
    title: 'Nenhum item encontrado',
    description: 'Não há dados para exibir no momento.',
    actionLabel: 'Adicionar Item',
    actionIcon: <AddIcon />
  },
  search: {
    title: 'Nenhum resultado encontrado',
    description: 'Não encontramos nada que corresponda à sua pesquisa.',
    actionLabel: null // Sem ação para search
  },
  error: {
    title: 'Erro ao carregar dados',
    description: 'Ocorreu um problema ao carregar as informações.',
    actionLabel: 'Tentar Novamente',
    actionIcon: <AddIcon />
  },
  tickets: {
    title: 'Nenhum ticket encontrado',
    description: 'Não há tickets cadastrados no momento.',
    actionLabel: 'Novo Ticket',
    actionIcon: <AddIcon />
  },
  campanhas: {
    title: 'Nenhuma campanha encontrada',
    description: 'Não há campanhas cadastradas.',
    actionLabel: 'Nova Campanha',
    actionIcon: <AddIcon />
  },
  contatos: {
    title: 'Nenhum contato encontrado',
    description: 'Sua lista de contatos está vazia.',
    actionLabel: 'Adicionar Contato',
    actionIcon: <AddIcon />
  },
  usuarios: {
    title: 'Nenhum usuário encontrado',
    description: 'Não há usuários cadastrados no sistema.',
    actionLabel: 'Novo Usuário',
    actionIcon: <AddIcon />
  },
  filas: {
    title: 'Nenhuma fila encontrada',
    description: 'Não há filas de atendimento configuradas.',
    actionLabel: 'Nova Fila',
    actionIcon: <AddIcon />
  },
  grupos: {
    title: 'Solicitações de Entrada',
    description: 'Selecione um grupo na lista para ver as solicitações',
    actionLabel: 'Novo Grupo',
    actionIcon: <AddIcon />
  }
};

// StandardEmptyState com APENAS UM BOTÃO
const StandardEmptyState = ({
  type = 'default',
  icon,
  title,
  description,
  primaryAction,
  size = 'medium',
  variant = 'default',
  customContent,
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));

  // Definir configurações baseadas no tipo
  const typeConfig = EmptyStateMessages[type] || EmptyStateMessages.default;
  const TypeIcon = EmptyStateIcons[type] || EmptyStateIcons.default;

  // Definir tamanhos baseados na prop size e breakpoint
  const getSizes = () => {
    const sizes = {
      small: {
        icon: isXs ? '2rem' : '2.5rem',
        title: isXs ? '1rem' : '1.125rem',
        description: '0.875rem',
        minHeight: isXs ? '180px' : '200px',
        padding: theme.spacing(2, 1)
      },
      medium: {
        icon: isXs ? '3rem' : '3.5rem',
        title: isXs ? '1.125rem' : '1.25rem',
        description: isXs ? '0.875rem' : '0.9rem',
        minHeight: isXs ? '250px' : '300px',
        padding: theme.spacing(isXs ? 3 : 4)
      },
      large: {
        icon: isXs ? '3.5rem' : '4rem',
        title: isXs ? '1.25rem' : '1.375rem',
        description: isXs ? '0.9rem' : '1rem',
        minHeight: isXs ? '300px' : '350px',
        padding: theme.spacing(isXs ? 4 : 6)
      }
    };
    return sizes[size] || sizes.medium;
  };

  const sizes = getSizes();

  // Definir estilos baseados na variante
  const getVariantStyles = () => {
    switch (variant) {
      case 'outlined':
        return {
          border: `2px dashed ${theme.palette.divider}`,
          borderRadius: 5,
          backgroundColor: theme.palette.background.default
        };
      case 'minimal':
        return {
          backgroundColor: 'transparent'
        };
      default:
        return {
          backgroundColor: theme.palette.background.paper,
          borderRadius: 5
        };
    }
  };

  // Determinar ação principal (APENAS UMA)
  const mainAction = primaryAction || (typeConfig.actionLabel ? {
    label: typeConfig.actionLabel,
    icon: typeConfig.actionIcon,
    onClick: () => console.log('Ação padrão'),
    variant: 'contained',
    color: 'primary'
  } : null);

  return (
    <Fade in timeout={500}>
      <EmptyStateContainer
        sx={{
          minHeight: sizes.minHeight,
          padding: sizes.padding,
          ...getVariantStyles(),
          ...sx
        }}
        {...props}
      >
        <ContentContainer>
          {/* Ícone */}
          <IconContainer>
            {icon || <TypeIcon sx={{ fontSize: sizes.icon }} />}
          </IconContainer>

          {/* Título */}
          <Typography
            variant="h6"
            component="h3"
            color="text.primary"
            gutterBottom
            sx={{
              fontSize: sizes.title,
              fontWeight: 600,
              marginBottom: theme.spacing(1)
            }}
          >
            {title || typeConfig.title}
          </Typography>

          {/* Descrição */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: sizes.description,
              lineHeight: 1.6,
              marginBottom: theme.spacing(2)
            }}
          >
            {description || typeConfig.description}
          </Typography>

          {/* Conteúdo Customizado */}
          {customContent && (
            <Box sx={{ mb: 2 }}>
              {customContent}
            </Box>
          )}

          {/* APENAS UM BOTÃO - Padrão do cabeçalho */}
          {mainAction && (
            <ActionContainer>
              <StandardButton
                variant={mainAction.variant || 'contained'}
                color={mainAction.color || 'primary'}
                startIcon={mainAction.icon}
                onClick={mainAction.onClick}
                disabled={mainAction.disabled}
                sx={{
                  // Garante que usa o padrão do cabeçalho
                  borderRadius: '5px !important',
                  ...mainAction.sx
                }}
              >
                {mainAction.label}
              </StandardButton>
            </ActionContainer>
          )}
        </ContentContainer>
      </EmptyStateContainer>
    </Fade>
  );
};

StandardEmptyState.propTypes = {
  type: PropTypes.oneOf([
    'default', 'search', 'error', 'offline', 'tickets', 'campanhas', 
    'contatos', 'usuarios', 'filas', 'grupos'
  ]),
  icon: PropTypes.node,
  title: PropTypes.string,
  description: PropTypes.string,
  primaryAction: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    icon: PropTypes.node,
    disabled: PropTypes.bool,
    color: PropTypes.string,
    variant: PropTypes.string,
    sx: PropTypes.object
  }),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  variant: PropTypes.oneOf(['default', 'outlined', 'minimal']),
  customContent: PropTypes.node,
  sx: PropTypes.object
};

export default StandardEmptyState;